<!-- 샘플비/패턴비 원장 (2026-07-17, B-1)
     디자인실 엑셀(사내샘플실/외주샘플/패턴)의 시스템 대체.
     사내 지급단가=FIX×50% 자동, 스타일 매칭→샘플요청 간이 생성(b안). -->
<template>
  <div class="page-container sample-cost-view">
    <div class="page-header">
      <h2>샘플비/패턴비</h2>
      <div class="header-actions">
        <el-button type="primary" @click="openAdd">행 추가</el-button>
        <el-button @click="downloadExcel" :loading="tplLoading">엑셀 다운로드</el-button>
        <el-button @click="openExcelDialog">엑셀 업로드</el-button>
        <el-button @click="helpVisible = true">도움말</el-button>
      </div>
      <HelpPanel v-model="helpVisible" :content-path="'screens/sample-cost'" />
    </div>

    <el-tabs v-model="activeTab">
      <!-- 원장 탭 -->
      <el-tab-pane label="원장" name="ledger">
        <el-row :gutter="10" class="mb-2" align="middle">
          <el-col :span="3">
            <el-input v-model="filters.closing_month" placeholder="마감월 2026-07" clearable @change="fetchList" />
          </el-col>
          <el-col :span="3">
            <el-select v-model="filters.work_type" placeholder="구분" clearable @change="fetchList" style="width:100%">
              <el-option label="샘플" value="SAMPLE" />
              <el-option label="패턴" value="PATTERN" />
            </el-select>
          </el-col>
          <el-col :span="3">
            <el-select v-model="filters.source" placeholder="사내/외주" clearable @change="fetchList" style="width:100%">
              <el-option label="사내" value="INHOUSE" />
              <el-option label="외주" value="OUT" />
            </el-select>
          </el-col>
          <el-col :span="5">
            <el-input v-model="filters.q" placeholder="스타일/품명/작업자/업체/지결 검색" clearable @change="fetchList" />
          </el-col>
          <el-col :span="4">
            <el-checkbox v-model="unlinkedOnly" @change="fetchList">스타일 미연결만</el-checkbox>
          </el-col>
          <el-col :span="6" style="text-align:right">
            <span class="hint">{{ rows.length }}건 — 사내샘플 지급단가는 FIX×50% 자동</span>
          </el-col>
        </el-row>

        <el-table :data="rows" v-loading="loading" size="small" border stripe max-height="560">
          <el-table-column label="구분" width="76" align="center">
            <template #default="{ row }">
              <el-tag size="small" :type="row.work_type === 'SAMPLE' ? 'primary' : 'warning'" effect="dark">
                {{ row.work_type === 'SAMPLE' ? '샘플비' : '패턴비' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="사내/외주" width="72" align="center">
            <template #default="{ row }">{{ row.source_display }}</template>
          </el-table-column>
          <el-table-column prop="date" label="날짜" width="100" />
          <el-table-column prop="closing_month" label="마감월" width="80" />
          <el-table-column prop="brand_name" label="브랜드" width="100" />
          <el-table-column label="스타일번호" min-width="130">
            <template #default="{ row }">
              {{ row.style_number }}
              <el-tag v-if="!row.design" size="small" type="info" effect="plain">미연결</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="item_name" label="품명" min-width="110" />
          <el-table-column prop="quantity" label="수량" width="60" align="right" />
          <el-table-column label="FIX/단가" width="90" align="right">
            <template #default="{ row }">
              {{ Number(row.source === 'OUT' ? row.unit_price : row.fix_price).toLocaleString() }}
            </template>
          </el-table-column>
          <el-table-column label="합계" width="100" align="right">
            <template #default="{ row }">{{ Number(row.total_amount).toLocaleString() }}</template>
          </el-table-column>
          <el-table-column label="지급합계" width="100" align="right">
            <template #default="{ row }">
              {{ row.source === 'INHOUSE' ? Number(row.pay_total).toLocaleString() : '' }}
            </template>
          </el-table-column>
          <el-table-column label="작업자/업체" width="110">
            <template #default="{ row }">{{ row.source === 'INHOUSE' ? row.worker : row.supplier_name }}</template>
          </el-table-column>
          <el-table-column prop="expense_no" label="지결번호" width="90" />
          <el-table-column prop="manager" label="담당" width="80" />
          <el-table-column label="" width="60" align="center">
            <template #default="{ row }">
              <el-button link type="primary" size="small" @click="openEdit(row)">수정</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- 월마감 탭 -->
      <el-tab-pane label="월마감" name="closing">
        <el-row :gutter="10" class="mb-2" align="middle">
          <el-col :span="3">
            <el-input v-model="closingMonth" placeholder="2026-07" @change="fetchSummary" />
          </el-col>
          <el-col :span="4">
            <el-button type="primary" size="small" @click="fetchSummary" :loading="summaryLoading">집계</el-button>
          </el-col>
        </el-row>
        <template v-if="summary">
          <el-row :gutter="10" class="mb-2">
            <el-col :span="5"><el-card shadow="never"><div class="kv">전체 {{ summary.total.count }}건</div>
              <div class="kv-big">{{ Number(summary.total.amount).toLocaleString() }}원</div></el-card></el-col>
            <el-col :span="5"><el-card shadow="never"><div class="kv">사내 지급 합계</div>
              <div class="kv-big">{{ Number(summary.total.pay).toLocaleString() }}원</div></el-card></el-col>
            <el-col :span="5"><el-card shadow="never"><div class="kv">지결 연결</div>
              <div class="kv-big">{{ summary.total.expense_linked }}/{{ summary.total.count }}</div></el-card></el-col>
            <el-col :span="5"><el-card shadow="never"><div class="kv">스타일 연결</div>
              <div class="kv-big">{{ summary.total.design_linked }}/{{ summary.total.count }}</div></el-card></el-col>
          </el-row>
          <el-row :gutter="12">
            <el-col :span="8">
              <div class="sec-title">구분별</div>
              <el-table :data="summary.by_type" size="small" border>
                <el-table-column label="구분" width="110">
                  <template #default="{ row }">
                    {{ (row.work_type === 'SAMPLE' ? '샘플' : '패턴') + '/' + (row.source === 'INHOUSE' ? '사내' : '외주') }}
                  </template>
                </el-table-column>
                <el-table-column prop="count" label="건수" width="60" align="right" />
                <el-table-column label="합계" align="right">
                  <template #default="{ row }">{{ Number(row.total || 0).toLocaleString() }}</template>
                </el-table-column>
              </el-table>
            </el-col>
            <el-col :span="8">
              <div class="sec-title">사내 작업자별 지급 명세</div>
              <el-table :data="summary.workers" size="small" border>
                <el-table-column prop="worker" label="작업자" />
                <el-table-column prop="count" label="건수" width="60" align="right" />
                <el-table-column label="지급액" align="right">
                  <template #default="{ row }">{{ Number(row.pay || 0).toLocaleString() }}</template>
                </el-table-column>
              </el-table>
            </el-col>
            <el-col :span="8">
              <div class="sec-title">외주 협력업체별 소계</div>
              <el-table :data="summary.suppliers" size="small" border>
                <el-table-column prop="supplier_name" label="업체" />
                <el-table-column prop="count" label="건수" width="60" align="right" />
                <el-table-column label="금액" align="right">
                  <template #default="{ row }">{{ Number(row.total || 0).toLocaleString() }}</template>
                </el-table-column>
              </el-table>
            </el-col>
          </el-row>
        </template>
      </el-tab-pane>
    </el-tabs>

    <!-- 행 추가/수정 다이얼로그 -->
    <el-dialog v-model="dialogVisible" :title="form.id ? '수정' : '행 추가'" width="680px"
               :close-on-click-modal="false" destroy-on-close>
      <el-form :model="form" label-width="86px" size="small">
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="구분">
              <el-select v-model="form.work_type" style="width:100%">
                <el-option label="샘플" value="SAMPLE" /><el-option label="패턴" value="PATTERN" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="사내/외주">
              <el-select v-model="form.source" style="width:100%">
                <el-option label="사내" value="INHOUSE" /><el-option label="외주" value="OUT" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="날짜">
              <el-date-picker v-model="form.date" type="date" value-format="YYYY-MM-DD" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="스타일번호">
              <el-select v-model="form.style_number" filterable remote clearable allow-create
                         default-first-option :remote-method="searchStyles" :loading="styleSearching"
                         placeholder="검색 또는 직접 입력" style="width:100%" @change="onStylePicked">
                <el-option v-for="o in styleOptions" :key="o.id" :value="o.number"
                           :label="o.number">
                  <span>{{ o.number }}</span>
                  <span style="float:right; color:#909399; font-size:12px">
                    {{ [o.brand_name, o.product_name, o.season].filter(Boolean).join(' · ') }}
                  </span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="품명"><el-input v-model="form.item_name" /></el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="수량"><el-input-number v-model="form.quantity" :min="1" style="width:100%" /></el-form-item>
          </el-col>
          <template v-if="form.source === 'INHOUSE'">
            <el-col :span="8">
              <el-form-item label="기본가격"><el-input-number v-model="form.base_price" :min="0" :step="10000" style="width:100%" /></el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="부속추가"><el-input-number v-model="form.addon_price" :min="0" :step="10000" style="width:100%" /></el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="작업자"><el-input v-model="form.worker" /></el-form-item>
            </el-col>
          </template>
          <template v-else>
            <el-col :span="8">
              <el-form-item label="단가"><el-input-number v-model="form.unit_price" :min="0" :step="10000" style="width:100%" /></el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="협력업체"><el-input v-model="form.supplier_name" /></el-form-item>
            </el-col>
          </template>
          <el-col :span="8">
            <el-form-item label="담당자"><el-input v-model="form.manager" /></el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="시즌"><el-input v-model="form.season" placeholder="26FW" /></el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="지결번호"><el-input v-model="form.expense_no" /></el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="비고"><el-input v-model="form.remarks" /></el-form-item>
          </el-col>
        </el-row>
        <div class="hint">FIX단가·지급단가(사내샘플 50%)·합계·마감월은 저장 시 자동 계산됩니다.
          스타일번호는 검색해 선택하면 품명·시즌이 자동으로 채워지고(빈칸만), 목록에 없는
          과거 스타일은 그대로 타이핑해도 됩니다 — 번호가 시스템 스타일과 일치하면 자동 연결됩니다.</div>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">취소</el-button>
        <el-button type="primary" :loading="saving" @click="save">저장</el-button>
      </template>
    </el-dialog>

    <!-- 엑셀 업로드 다이얼로그 -->
    <el-dialog v-model="excelDialogVisible" title="원장 엑셀 업로드" width="600px"
               :close-on-click-modal="false" destroy-on-close>
      <div class="hint" style="margin-bottom:6px">
        [엑셀 다운로드] 서식(원장 시트)에 기존 엑셀 4개 시트 내용을 붙여넣어 업로드하세요.
        중복 행(구분+날짜+스타일+금액)은 자동으로 건너뜁니다 — 재업로드 안전.
      </div>
      <input type="file" accept=".xlsx" @change="onExcelFileChange" style="margin: 10px 0;" />
      <template v-if="excelReport">
        <el-alert :type="excelReportHasError ? 'warning' : 'success'" :closable="false" show-icon
                  :title="excelReport.dry_run ? '검증 결과 (아직 반영 안 됨)' : '반영 완료'">
          <div>{{ excelReport.costs.created.join(' / ') }} · 오류 {{ excelReport.costs.errors.length }}</div>
        </el-alert>
        <div v-for="(err, i) in excelReport.costs.errors" :key="i"
             style="font-size:12px; color:#e6a23c; margin-top:4px">⚠ {{ err }}</div>
      </template>
      <template #footer>
        <el-button @click="excelDialogVisible = false">닫기</el-button>
        <el-button type="primary" plain :disabled="!excelFile" :loading="excelUploading"
                   @click="uploadExcel(false)">검증</el-button>
        <el-button type="primary"
                   :disabled="!excelFile || !excelReport || !excelReport.dry_run || excelReportHasError"
                   :loading="excelUploading" @click="uploadExcel(true)">반영</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import HelpPanel from '@/components/help/HelpPanel.vue'
const helpVisible = ref(false)

const activeTab = ref('ledger')
const rows = ref([])
const loading = ref(false)
const filters = ref({ closing_month: '', work_type: '', source: '', q: '' })
const unlinkedOnly = ref(false)

const fetchList = async () => {
  loading.value = true
  try {
    const params = {}
    for (const [k, v] of Object.entries(filters.value)) if (v) params[k] = v
    if (unlinkedOnly.value) params.unlinked = '1'
    const res = await axiosInstance.get('/sample-costs/', { params })
    rows.value = res.data.results || res.data || []
  } catch (e) {
    console.error('원장 조회 실패:', e)
  } finally {
    loading.value = false
  }
}

// ── 행 추가/수정 ──
const dialogVisible = ref(false)
const saving = ref(false)
const emptyForm = () => ({
  id: null, work_type: 'SAMPLE', source: 'INHOUSE', date: '', style_number: '',
  item_name: '', quantity: 1, base_price: 0, addon_price: 0, unit_price: 0,
  worker: '', supplier_name: '', manager: '', season: '', expense_no: '', remarks: '',
})
const form = ref(emptyForm())
const openAdd = () => { form.value = emptyForm(); styleOptions.value = []; dialogVisible.value = true }

// ── 스타일 원격 검색 + 자동 채움 ──
const styleOptions = ref([])
const styleSearching = ref(false)
const searchStyles = async (q) => {
  if (!q || q.length < 2) { styleOptions.value = []; return }
  styleSearching.value = true
  try {
    const res = await axiosInstance.get('/sample-costs/style-search/', { params: { q } })
    styleOptions.value = res.data || []
  } catch (e) {
    console.error('스타일 검색 실패:', e)
  } finally {
    styleSearching.value = false
  }
}
const onStylePicked = (val) => {
  const o = styleOptions.value.find(x => x.number === val)
  if (!o) return  // 직접 입력(미등록 스타일) — 그대로 둠
  if (!form.value.item_name) form.value.item_name = o.product_name || ''
  if (!form.value.season) form.value.season = o.season || ''
}
const openEdit = (row) => { form.value = { ...row }; styleOptions.value = []; dialogVisible.value = true }
const save = async () => {
  saving.value = true
  try {
    if (form.value.id) {
      await axiosInstance.put(`/sample-costs/${form.value.id}/`, form.value)
    } else {
      await axiosInstance.post('/sample-costs/', form.value)
    }
    ElMessage.success('저장되었습니다')
    dialogVisible.value = false
    fetchList()
  } catch (e) {
    console.error('저장 실패:', e)
    ElMessage.error('저장에 실패했습니다')
  } finally {
    saving.value = false
  }
}

// ── 월마감 ──
const closingMonth = ref('')
const summary = ref(null)
const summaryLoading = ref(false)
const fetchSummary = async () => {
  if (!closingMonth.value) { ElMessage.warning('마감월(YYYY-MM)을 입력하세요'); return }
  summaryLoading.value = true
  try {
    const res = await axiosInstance.get('/sample-costs/summary/',
      { params: { closing_month: closingMonth.value } })
    summary.value = res.data
  } catch (e) {
    console.error('집계 실패:', e)
    ElMessage.error('집계에 실패했습니다')
  } finally {
    summaryLoading.value = false
  }
}

// ── 엑셀 ──
const tplLoading = ref(false)
const excelDialogVisible = ref(false)
const excelUploading = ref(false)
const excelFile = ref(null)
const excelReport = ref(null)
const excelReportHasError = computed(() =>
  !!excelReport.value && excelReport.value.costs.errors.length > 0)

const downloadExcel = async () => {
  tplLoading.value = true
  try {
    const params = {}
    for (const [k, v] of Object.entries(filters.value)) if (v) params[k] = v
    const res = await axiosInstance.get('/sample-costs/excel-template/',
      { params, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `샘플패턴비_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('엑셀 다운로드에 실패했습니다')
  } finally {
    tplLoading.value = false
  }
}
const openExcelDialog = () => { excelFile.value = null; excelReport.value = null; excelDialogVisible.value = true }
const onExcelFileChange = (e) => { excelFile.value = e.target.files?.[0] || null; excelReport.value = null }
const uploadExcel = async (apply) => {
  if (!excelFile.value) return
  excelUploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', excelFile.value)
    const res = await axiosInstance.post(
      `/sample-costs/excel-import/${apply ? '?apply=1' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
    excelReport.value = res.data
    if (apply) {
      ElMessage.success('반영되었습니다')
      fetchList()
    }
  } catch (e) {
    console.error('업로드 실패:', e)
    ElMessage.error(e.response?.data?.detail || '엑셀 업로드에 실패했습니다')
  } finally {
    excelUploading.value = false
  }
}

onMounted(fetchList)
</script>

<style scoped>
.page-container { padding: 20px; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.page-header h2 { margin: 0; }
.mb-2 { margin-bottom: 10px; }
.hint { font-size: 12px; color: #909399; }
.sec-title { font-weight: 600; margin-bottom: 6px; }
.kv { font-size: 12px; color: #909399; }
.kv-big { font-size: 18px; font-weight: 700; }
</style>
